import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { styled } from "@mui/material/styles";
import {
  selectGameId,
  selectGameStatusObject,
} from "../../gamestate/gameStateSlice";
import { selectPlayerSubmissionsForGameboardLeaderboard } from "../../question/submissionSlice";
import { setWagerAction } from "../../question/questionSlice";
import {
  GAMESTATE_ROUND,
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_FINALROUND_CATEGORY_PREVIEW,
  GAMESTATE_ROUNDSTATE_FINALROUND_WAGER_INSTRUCTIONS,
  GAMESTATE_ROUNDSTATE_QUESTION_WAGERING,
} from "../../gamestate/constants";

const Root = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  marginTop: theme.spacing(2),
}));

const WAGER_MONITOR_STATES = [
  GAMESTATE_ROUNDSTATE_FINALROUND_CATEGORY_PREVIEW,
  GAMESTATE_ROUNDSTATE_FINALROUND_WAGER_INSTRUCTIONS,
  GAMESTATE_ROUNDSTATE_QUESTION_WAGERING,
];

const WagerRow = ({ player, wager, maxWager, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(wager ?? "");

  const startEdit = () => {
    setEditValue(wager ?? "");
    setEditing(true);
  };

  const saveEdit = async () => {
    const parsed = parseInt(editValue, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed !== wager) {
      await onSave(player.id, parsed);
    }
    setEditing(false);
  };

  const cancelEdit = () => setEditing(false);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") saveEdit();
    else if (e.key === "Escape") cancelEdit();
  };

  const hasWager = wager !== null && wager !== undefined;
  const overMax = hasWager && wager > maxWager;

  const wagerDisplay = editing ? (
    <TextField
      type="number"
      size="small"
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={saveEdit}
      onKeyDown={handleKeyDown}
      autoFocus
      variant="standard"
      sx={{ width: 120 }}
      inputProps={{ min: 0, max: maxWager }}
    />
  ) : (
    <span>
      {hasWager ? (
        <span style={{ color: overMax ? "#d32f2f" : undefined }}>
          {"$" + wager.toLocaleString()}
          {overMax && " (over max)"}
        </span>
      ) : (
        <span style={{ color: "#999", fontStyle: "italic" }}>not entered</span>
      )}
      <IconButton size="small" onClick={startEdit} sx={{ ml: 0.5 }}>
        <EditIcon fontSize="small" />
      </IconButton>
    </span>
  );

  const score = typeof player.score === "number" ? player.score : 0;

  return (
    <ListItem>
      <ListItemText
        primary={`${player.name} — $${score.toLocaleString()} max`}
        secondary={wagerDisplay}
      />
    </ListItem>
  );
};

const WagerMonitor = () => {
  const dispatch = useDispatch();
  const gameId = useSelector(selectGameId);
  const gameState = useSelector(selectGameStatusObject);
  const playerSubmissions = useSelector(
    selectPlayerSubmissionsForGameboardLeaderboard
  );

  const isFinalWagering =
    gameState?.currentState === GAMESTATE_ROUND &&
    gameState?.round === GAMESTATE_ROUND_FINAL &&
    WAGER_MONITOR_STATES.includes(gameState?.roundState);

  if (!isFinalWagering) return null;

  const questionId = gameState.questionId;

  const onSave = async (playerId, wager) => {
    await dispatch(setWagerAction(gameId, questionId, playerId, wager));
  };

  const players = playerSubmissions || [];

  return (
    <Root>
      <Typography variant="h6">Wagers</Typography>
      {players.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No players in game.
        </Typography>
      ) : (
        <List dense>
          {players.map((player) => {
            const wager = player.submission?.wager;
            const score = typeof player.score === "number" ? player.score : 0;
            return (
              <WagerRow
                key={player.id}
                player={player}
                wager={wager}
                maxWager={Math.max(0, score)}
                onSave={onSave}
              />
            );
          })}
        </List>
      )}
    </Root>
  );
};

export default WagerMonitor;
